
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunityMember } from '@/contexts/CommunityMemberContext';
import { toast } from '@/hooks/use-toast';

export type ReviewReply = {
  id: string;
  review_id: string;
  business_name: string;
  content: string;
  status: 'Pending Validation' | 'Published' | 'Rejected';
  created_at: string;
  updated_at: string;
};

interface ReviewReplyResponse {
  success: boolean;
  error?: string;
  id?: string;
}

export const useReviewReplies = (reviewId?: string) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isAllowedTo } = useCommunityMember();

  const replyQuery = useQuery({
    queryKey: ['reviewReply', reviewId],
    queryFn: async () => {
      if (!reviewId) return null;

      const { data, error } = await supabase
        .from('review_replies')
        .select('*')
        .eq('review_id', reviewId)
        .maybeSingle();

      if (error) {
        throw new Error(error.message);
      }

      return data as ReviewReply | null;
    },
    enabled: !!reviewId,
  });

  const canReplyQuery = useQuery({
    queryKey: ['canReply', reviewId, user?.id],
    queryFn: async () => {
      if (!reviewId || !user) return false;

      // Check if user owns the business for this review
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          business_id,
          businesses!inner(user_id)
        `)
        .eq('id', reviewId)
        .eq('status', 'Published')
        .single();

      if (error || !data) return false;

      return data.businesses.user_id === user.id;
    },
    enabled: !!reviewId && !!user,
  });

  const createReply = useMutation({
    mutationFn: async ({ reviewId, content }: { reviewId: string; content: string }) => {
      if (!user) {
        throw new Error('You must be logged in to reply to reviews');
      }

      const { data, error } = await supabase.rpc('create_review_reply', {
        p_review_id: reviewId,
        p_content: content
      });

      if (error) {
        throw new Error(error.message);
      }

      // Type assertion with proper checking
      const response = data as unknown as ReviewReplyResponse;
      if (!response || typeof response !== 'object' || !response.success) {
        throw new Error(response?.error || 'Failed to create reply');
      }

      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviewReply', reviewId] });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      toast({
        title: 'Reply submitted',
        description: 'Your reply has been submitted and is pending approval.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit reply',
        variant: 'destructive',
      });
    },
  });

  const updateReply = useMutation({
    mutationFn: async ({ replyId, content }: { replyId: string; content: string }) => {
      if (!user) {
        throw new Error('You must be logged in to update replies');
      }

      const { data, error } = await supabase.rpc('update_review_reply', {
        p_reply_id: replyId,
        p_content: content
      });

      if (error) {
        throw new Error(error.message);
      }

      // Type assertion with proper checking
      const response = data as unknown as ReviewReplyResponse;
      if (!response || typeof response !== 'object' || !response.success) {
        throw new Error(response?.error || 'Failed to update reply');
      }

      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviewReply', reviewId] });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      toast({
        title: 'Reply updated',
        description: 'Your reply has been updated and is pending approval.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update reply',
        variant: 'destructive',
      });
    },
  });

  const validateReply = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'Published' | 'Rejected' }) => {
      if (!user) {
        throw new Error('You must be logged in to validate replies');
      }

      const canValidate = await isAllowedTo('view_all_reviews');
      if (!canValidate) {
        throw new Error('You do not have permission to validate replies');
      }

      const { data, error } = await supabase
        .from('review_replies')
        .update({ status })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw new Error(error.message || 'Failed to validate reply');
      }

      return data as ReviewReply;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviewReply'] });
      queryClient.invalidateQueries({ queryKey: ['adminReplies'] });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to validate reply',
        variant: 'destructive',
      });
    },
  });

  const deleteReply = useMutation({
    mutationFn: async (replyId: string) => {
      if (!user) {
        throw new Error('You must be logged in to delete replies');
      }

      const { error } = await supabase
        .from('review_replies')
        .delete()
        .eq('id', replyId);

      if (error) {
        throw new Error(error.message || 'Failed to delete reply');
      }

      return replyId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviewReply', reviewId] });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      toast({
        title: 'Reply deleted',
        description: 'Your reply has been deleted.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete reply',
        variant: 'destructive',
      });
    },
  });

  return {
    reply: replyQuery.data,
    isLoading: replyQuery.isLoading,
    error: replyQuery.error,
    canReply: canReplyQuery.data || false,
    isCanReplyLoading: canReplyQuery.isLoading,
    createReply,
    updateReply,
    validateReply,
    deleteReply,
  };
};

// Hook for admin to fetch all pending replies
export const useAdminReviewReplies = () => {
  const { user } = useAuth();
  const { isAllowedTo } = useCommunityMember();

  return useQuery({
    queryKey: ['adminReplies'],
    queryFn: async () => {
      if (!user) return [];

      const canViewAll = await isAllowedTo('view_all_reviews');
      if (!canViewAll) return [];

      const { data, error } = await supabase
        .from('review_replies')
        .select(`
          *,
          reviews!inner(
            id,
            rating,
            description,
            display_name,
            created_at,
            businesses!inner(
              name,
              community_id
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
    enabled: !!user,
  });
};
